
/**
 * Scroll-driven Frame Sequence Hero Animation
 * Ultra-smooth: createImageBitmap pre-decoding, single-pipeline delta-time
 * lerp, adaptive velocity smoothing, priority frame loading.
 * Mac/Safari optimized: capped DPR, no cross-fade, paused when offscreen.
 * Dependencies: GSAP 3 + ScrollTrigger
 */
(function () {
  "use strict";

  /* ══ Site loader + logo-video gate (home) ══
     Register before any early return so logo-video-animation.js can always coordinate. */
  window.__yessHeroFrameReady = false;
  if (!document.getElementById("logo-video-section")) {
    window.__yessLogoVideoReady = true;
  }
  var _loaderDismissed = false;
  function tryDismissSiteLoader() {
    if (_loaderDismissed) return;
    if (!window.__yessHeroFrameReady) return;
    if (!window.__yessLogoVideoReady) return;
    _loaderDismissed = true;
    var loader = document.getElementById("site-loader");
    if (loader) {
      loader.classList.add("loader-hidden");
      loader.addEventListener(
        "transitionend",
        function () {
          if (loader.parentNode) loader.parentNode.removeChild(loader);
        },
        { once: true }
      );
    }
    document.body.classList.remove("site-loading");
    document.documentElement.classList.remove("site-loading");
  }
  window.__yessTryDismissSiteLoader = tryDismissSiteLoader;

  var section = document.getElementById("hero-frame-section");
  var canvas = document.getElementById("hero-frame-canvas");
  if (!section || !canvas) {
    var iframe = document.querySelector(".hero-vimeo-video");
    if (iframe) {
      var dismissed = false;
      function dismiss() {
        if (dismissed) return;
        dismissed = true;
        if (section) {
          section.classList.add("hero-frame--ready");
        }
        window.__yessHeroFrameReady = true;
        tryDismissSiteLoader();
      }

      window.addEventListener("message", function (event) {
        if (event.origin && event.origin.indexOf("vimeo.com") !== -1) {
          try {
            var data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
            if (data) {
              if (data.event === "ready") {
                if (iframe.contentWindow) {
                  [
                    "play",
                    "playing",
                    "timeupdate",
                    "bufferend"
                  ].forEach(function (eventName) {
                    iframe.contentWindow.postMessage(JSON.stringify({ method: "addEventListener", value: eventName }), "*");
                  });
                }
                return;
              }
              if (data.event === "play" || data.event === "playing" || data.event === "bufferend") {
                dismiss();
                return;
              }
              if (
                data.event === "timeupdate" &&
                data.data &&
                typeof data.data.seconds === "number" &&
                data.data.seconds > 0.02
              ) {
                dismiss();
                return;
              }
              if (data.event === "error" || (data.data && data.data.name === "PrivacyError")) {
                dismiss();
              }
            }
          } catch (e) { }
        }
      });

      // Global safety fallback: if the Vimeo player never delivers any usable play/load event.
      setTimeout(dismiss, 800);
    } else {
      window.__yessHeroFrameReady = true;
      tryDismissSiteLoader();
    }
    return;
  }
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
    console.warn("[hero-frames] GSAP or ScrollTrigger not loaded");
    window.__yessHeroFrameReady = true;
    tryDismissSiteLoader();
    return;
  }
  gsap.registerPlugin(ScrollTrigger);

  /* ═══════════════════════════════════════════
     PLATFORM DETECTION
     ═══════════════════════════════════════════ */
  var ua = navigator.userAgent || "";
  var isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  var isMac = /Macintosh|MacIntel|MacPPC|Mac68K/i.test(navigator.platform || ua);
  var isLowPowerGPU = isMac || isSafari;

  /* RTL (Arabic) detection — mirrors the hero so the character sits on the
     LEFT and Arabic text on the RIGHT, and flips slide-in/out directions.
     Checks <html dir>, body class, and the language-switcher localStorage
     key as a fallback in case this script runs before the switcher boot. */
  var _storedLang = "";
  try { _storedLang = localStorage.getItem("yes_site_lang") || ""; } catch (e) { /* noop */ }
  var isRTL = (document.documentElement.getAttribute("dir") || "").toLowerCase() === "rtl"
    || (document.body && document.body.classList && document.body.classList.contains("lang-ar"))
    || _storedLang === "ar";
  var dirSign = isRTL ? -1 : 1; // multiply X-translations by this to mirror

  /* ═══════════════════════════════════════════
     LENIS SMOOTH SCROLL
     Mac trackpads fire very high-frequency,
     tiny-delta scroll events — use a shorter
     duration so Lenis doesn't fight the
     trackpad's own momentum.
     ═══════════════════════════════════════════ */
  if (typeof Lenis !== "undefined" && !window._lenisInstance) {
    var lenis = new Lenis({
      duration: isMac ? 0.8 : 1.2,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
      wheelMultiplier: isMac ? 0.8 : 1,
      smoothTouch: false,
      touchMultiplier: 2
    });
    window._lenisInstance = lenis;

    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);
  }

  var TOTAL_FRAMES = 41;
  var LAST = TOTAL_FRAMES - 1;
  var FRAME_SCROLL = 1200;
  var mobile = window.matchMedia("(max-width: 768px)").matches;
  var smallScreen = window.matchMedia("(max-width: 1024px)").matches;
  var EXTRA_SCROLL = smallScreen ? 300 : 5000;
  var SCROLL_PX = FRAME_SCROLL + EXTRA_SCROLL;
  var FRAME_END = FRAME_SCROLL / SCROLL_PX;
  /* Slightly softer curve so mid/late frames don't rush past; pairs better with fewer frames (e.g. 41). */
  var FRAME_MAP_EASE_POWER = 1.85;

  var mqSmall = window.matchMedia("(max-width: 1024px)");
  mqSmall.addEventListener("change", function () {
    window.location.reload();
  });

  var FRAME_BASE = (function () {
    var tags = document.querySelectorAll(
      'script[src*="/assets/"],link[href*="/assets/"]'
    );
    for (var i = 0; i < tags.length; i++) {
      var url = tags[i].src || tags[i].href || "";
      var idx = url.indexOf("/assets/");
      if (idx >= 0) return url.substring(0, idx + 8) + "images/frames/frame_";
    }
    return "/assets/images/frames/frame_";
  })();

  /* ═══════════════════════════════════════════
     CANVAS  (desktop)  /  IMG FALLBACK (mobile)
     ═══════════════════════════════════════════ */
  var canvas = document.getElementById("hero-frame-canvas");
  if (!canvas) return;

  var useImgFallback = smallScreen;
  var heroImg = null;
  var ctx, dpr;

  var MAX_DPR = isLowPowerGPU ? 1.5 : 2;
  var SMOOTH_QUALITY = isLowPowerGPU ? "medium" : "high";

  if (useImgFallback) {
    canvas.style.display = "none";
    heroImg = document.createElement("img");
    heroImg.className = "hero-frame-img";
    heroImg.alt = "";
    heroImg.draggable = false;
    section.insertBefore(heroImg, canvas);
  } else {
    ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
    dpr = Math.min(window.devicePixelRatio, MAX_DPR);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = SMOOTH_QUALITY;
  }

  /* Arabic: for the mobile fallback <img>, a CSS scaleX(-1) is enough
     (handled in language-switcher.css). For the desktop <canvas>, we mirror
     the pixels at DRAW time inside renderExact() — applying a CSS transform
     to the canvas is unreliable here because the later xPercent slide-out
     tween needs to compose with scaleX, and `contain: layout size` interacts
     poorly with negative scales on some browsers. */

  function resizeCanvas() {
    if (useImgFallback) return;
    var w = section.clientWidth;
    var h = section.clientHeight;
    if (w === 0 || h === 0) return;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = SMOOTH_QUALITY;
    needsRender = true;
  }
  resizeCanvas();

  /* ═══════════════════════════════════════════
     FRAME PRELOADING  –  createImageBitmap +
     priority load order (keyframes first)
     ═══════════════════════════════════════════ */
  var frames = new Array(TOTAL_FRAMES);
  var fallbackImgs = useImgFallback ? new Array(TOTAL_FRAMES) : null;
  var loaded = 0;
  var firstReady = false;
  var useBitmap = !useImgFallback && typeof createImageBitmap === "function";

  function pad(n) {
    var s = String(n);
    while (s.length < 4) s = "0" + s;
    return s;
  }

  function frameUrl(frameNum) {
    return FRAME_BASE + pad(frameNum) + ".jpg";
  }

  function buildLoadOrder() {
    var order = [];
    var added = {};
    function push(i) {
      if (i >= 0 && i < TOTAL_FRAMES && !added[i]) {
        added[i] = true;
        order.push(i);
      }
    }
    push(0);
    push(LAST);
    var step;
    for (step = 8; step >= 1; step = step >> 1) {
      for (var j = 0; j < TOTAL_FRAMES; j += step) push(j);
    }
    for (var k = 0; k < TOTAL_FRAMES; k++) push(k);
    return order;
  }
  var loadOrder = buildLoadOrder();

  function onFrameReady(index) {
    loaded++;
    if (!firstReady && isReady(0)) {
      firstReady = true;
      window.__yessHeroFrameReady = true;
      if (useImgFallback && heroImg) {
        heroImg.src = fallbackImgs[0].src;
        heroImg.setAttribute("data-f", "0");
      } else {
        resizeCanvas();
        renderExact(0);
      }
      section.classList.add("hero-frame--ready");
      tryDismissSiteLoader();
    }
    if (loaded >= TOTAL_FRAMES) {
      section.classList.add("hero-frame--all-loaded");
      wakeLoop();
    }
  }

  function loadFrameBitmap(index) {
    var url = frameUrl(index + 1);
    fetch(url)
      .then(function (r) { return r.blob(); })
      .then(function (blob) { return createImageBitmap(blob); })
      .then(function (bmp) {
        frames[index] = bmp;
        onFrameReady(index);
      })
      .catch(function () {
        onFrameReady(index);
      });
  }

  function loadFrameImage(index) {
    var img = new Image();
    img.decoding = "async";
    img.onload = function () {
      if (useImgFallback) {
        fallbackImgs[index] = img;
      }
      frames[index] = img;
      onFrameReady(index);
    };
    img.onerror = function () { onFrameReady(index); };
    img.src = frameUrl(index + 1);
  }

  var BATCH_SIZE = isLowPowerGPU ? 4 : 6;
  var BATCH_DELAY = isLowPowerGPU ? 16 : 0;
  var loadIdx = 0;

  function loadNextBatch() {
    for (var b = 0; b < BATCH_SIZE && loadIdx < loadOrder.length; b++, loadIdx++) {
      var fi = loadOrder[loadIdx];
      if (useBitmap) {
        loadFrameBitmap(fi);
      } else {
        loadFrameImage(fi);
      }
    }
    if (loadIdx < loadOrder.length) {
      setTimeout(loadNextBatch, BATCH_DELAY);
    }
  }
  loadNextBatch();

  setTimeout(function () {
    if (!firstReady && isReady(0)) {
      firstReady = true;
      window.__yessHeroFrameReady = true;
      section.classList.add("hero-frame--ready");
      tryDismissSiteLoader();
    }
    if (firstReady) {
      showFrame(Math.round(currentFrame) || 0);
    }
    if (!useImgFallback) resizeCanvas();
  }, 400);

  // Safety: dismiss loader after 6s even if frames fail to load
  setTimeout(function () {
    if (!firstReady) {
      section.classList.add("hero-frame--ready");
    }
    window.__yessHeroFrameReady = true;
    tryDismissSiteLoader();
  }, 6000);

  // If logo-video init stalls, do not block the site forever
  setTimeout(function () {
    if (!window.__yessLogoVideoReady) {
      window.__yessLogoVideoReady = true;
      tryDismissSiteLoader();
    }
  }, 12000);

  /* ═══════════════════════════════════════════
     DRAW HELPERS
     ═══════════════════════════════════════════ */
  var PAN_PERCENT = mobile ? 0.10 : 0.01;
  var CHAR_SCALE = 0.88;
  var CHAR_SHIFT_Y = 0.03;
  var targetFrame = 0;
  var currentFrame = 0;
  var scrollProg = 0;
  var smoothProg = 0;

  var SNAP_THRESHOLD = 0.006;
  var PAN_SMOOTH_SPEED = 11;
  var SMOOTH_MIN = 6;
  var SMOOTH_MAX = 17;

  function dtLerp(current, target, speed, dt) {
    return current + (target - current) * (1 - Math.exp(-speed * dt));
  }

  function frameProgressFromScroll(scrollProgress) {
    var raw = Math.max(0, Math.min(1, scrollProgress / FRAME_END));
    return 1 - Math.pow(1 - raw, FRAME_MAP_EASE_POWER);
  }

  function isReady(idx) {
    if (idx < 0 || idx > LAST) return false;
    var f = frames[idx];
    if (!f) return false;
    if (typeof ImageBitmap !== "undefined" && f instanceof ImageBitmap) return true;
    return f.complete && f.naturalWidth > 0;
  }

  function imgWidth(f) {
    return (typeof ImageBitmap !== "undefined" && f instanceof ImageBitmap) ? f.width : f.naturalWidth;
  }
  function imgHeight(f) {
    return (typeof ImageBitmap !== "undefined" && f instanceof ImageBitmap) ? f.height : f.naturalHeight;
  }

  function coverParams(img, panOffset) {
    var cw = canvas.width;
    var ch = canvas.height;
    var iw = imgWidth(img);
    var ih = imgHeight(img);
    var totalPan = PAN_PERCENT * cw;
    var safety = 4 * dpr;

    var baseScale = Math.max(cw / iw, ch / ih);
    var minWidth = cw + totalPan + safety;
    var scale = iw * baseScale < minWidth ? minWidth / iw : baseScale;
    if (ih * scale < ch) scale = ch / ih;
    var nw = iw * scale;
    var nh = ih * scale;

    var dx = -panOffset * totalPan;
    var dy = (ch - nh) / 2;

    return { dx: dx, dy: dy, nw: nw, nh: nh };
  }

  function showFrame(idx) {
    if (useImgFallback && heroImg) {
      if (isReady(idx) && heroImg.getAttribute("data-f") !== String(idx)) {
        var src = fallbackImgs && fallbackImgs[idx] ? fallbackImgs[idx].src : (frames[idx] && frames[idx].src);
        if (src) {
          heroImg.src = src;
          heroImg.setAttribute("data-f", idx);
        }
      }
    } else {
      renderExact(idx);
    }
  }

  var lastRenderedFrame = -999;

  function renderExact(frameFloat) {
    frameFloat = Math.max(0, Math.min(LAST, frameFloat));
    var lo = Math.floor(frameFloat);
    var hi = Math.min(LAST, lo + 1);
    var blendT = frameFloat - lo;

    if (!isReady(lo)) {
      if (isReady(hi)) lo = hi;
      else return;
    }
    if (hi !== lo && !isReady(hi)) hi = lo;

    if (
      Math.abs(frameFloat - lastRenderedFrame) < 0.0004 &&
      Math.abs(scrollProg - smoothProg) < 0.0005
    ) {
      return;
    }
    lastRenderedFrame = frameFloat;

    var pLo = coverParams(frames[lo], smoothProg);
    ctx.save();
    if (isRTL) {
      /* Flip the drawing coordinate system so the character (baked on the
         right of every source frame) appears on the LEFT in Arabic. */
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.globalAlpha = 1;
    ctx.drawImage(frames[lo], pLo.dx, pLo.dy, pLo.nw, pLo.nh);
    if (hi !== lo && blendT > 0.001 && isReady(hi)) {
      var pHi = coverParams(frames[hi], smoothProg);
      ctx.globalAlpha = Math.min(1, Math.max(0, blendT));
      ctx.drawImage(frames[hi], pHi.dx, pHi.dy, pHi.nw, pHi.nh);
    }
    ctx.restore();
    ctx.globalAlpha = 1;
    currentFrame = frameFloat;
  }

  /* ═══════════════════════════════════════════
     SMOOTH RAF RENDER LOOP
     Single-pipeline delta-time lerp with
     adaptive velocity-based smoothing speed.
     Pauses automatically when hero is offscreen.
     ═══════════════════════════════════════════ */
  var rafId = 0;
  var needsRender = true;
  var lastTime = 0;
  var isVisible = true;
  var idleFrames = 0;
  var MAX_IDLE = 10;

  var visObserver = new IntersectionObserver(function (entries) {
    isVisible = entries[0].isIntersecting;
    if (isVisible && !rafId) {
      lastTime = 0;
      idleFrames = 0;
      rafId = requestAnimationFrame(tick);
    }
  }, { rootMargin: "100px 0px" });
  visObserver.observe(section);

  function tick(timestamp) {
    if (!isVisible) {
      rafId = 0;
      return;
    }

    var dt = lastTime ? Math.min((timestamp - lastTime) / 1000, 0.05) : 0.016;
    lastTime = timestamp;

    var frameDiff = targetFrame - currentFrame;
    var absDiff = Math.abs(frameDiff);
    var didWork = false;

    if (absDiff > SNAP_THRESHOLD) {
      var velocity = absDiff / dt;
      var speed = velocity > 200 ? SMOOTH_MAX
        : velocity > 60 ? SMOOTH_MIN + (SMOOTH_MAX - SMOOTH_MIN) * ((velocity - 60) / 140)
          : SMOOTH_MIN;
      currentFrame = dtLerp(currentFrame, targetFrame, speed, dt);
      needsRender = true;
      didWork = true;
    } else if (absDiff > 0.0005) {
      currentFrame = targetFrame;
      needsRender = true;
      didWork = true;
    }

    var progDiff = scrollProg - smoothProg;
    if (Math.abs(progDiff) > 0.00005) {
      smoothProg = dtLerp(smoothProg, scrollProg, PAN_SMOOTH_SPEED, dt);
      needsRender = true;
      didWork = true;
    }

    if (needsRender) {
      if (useImgFallback) {
        var idx = Math.round(currentFrame);
        idx = Math.max(0, Math.min(LAST, idx));
        showFrame(idx);
      } else {
        renderExact(currentFrame);
      }
      needsRender = false;
      didWork = true;
    }

    idleFrames = didWork ? 0 : idleFrames + 1;
    if (idleFrames < MAX_IDLE) {
      rafId = requestAnimationFrame(tick);
    } else {
      rafId = 0;
    }
  }
  rafId = requestAnimationFrame(tick);

  function wakeLoop() {
    idleFrames = 0;
    needsRender = true;
    if (!rafId && isVisible) {
      lastTime = 0;
      rafId = requestAnimationFrame(tick);
    }
  }

  /* ═══════════════════════════════════════════
     TEXT ELEMENTS
     ═══════════════════════════════════════════ */
  var phaseStart = section.querySelector(".hero-frame-phase--start");
  var phaseEnd = section.querySelector(".hero-frame-phase--end");
  var discover = section.querySelector(".hero-frame-discover");

  /* ═══════════════════════════════════════════
     MOBILE / TABLET: move video-text below hero
     BEFORE ScrollTrigger pins the section
     ═══════════════════════════════════════════ */
  var phaseVideoText = document.getElementById("hero-frame-video-text");
  if (phaseVideoText && smallScreen) {
    section.parentNode.insertBefore(phaseVideoText, section.nextSibling);
    phaseVideoText.classList.add("hero-frame-video-text--static");
    var earlyVid = phaseVideoText.querySelector("video");
    if (earlyVid) earlyVid.play().catch(function () { });
  }

  /* ═══════════════════════════════════════════
     GSAP SCROLLTRIGGER TIMELINE
     scrub: true  →  1:1 mapping to Lenis smooth
     scroll; dtLerp handles frame interpolation.
     ═══════════════════════════════════════════ */
  var tl = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      start: "top top",
      end: "+=" + SCROLL_PX,
      pin: true,
      // Numeric scrub smooths both forward/backward catch-up,
      // so entering this pinned section from below feels continuous.
      scrub: 0.72,
      // Higher anticipatePin reduces the perceived "snap" at pin boundaries.
      anticipatePin: 3,
      fastScrollEnd: false,
      invalidateOnRefresh: true,
      onUpdate: function (self) {
        var p = self.progress;
        scrollProg = p;
        var frameP = frameProgressFromScroll(p);
        targetFrame = frameP * LAST;
        wakeLoop();
        section.style.setProperty("--frame-progress", p.toFixed(4));
      },
      onEnterBack: function (self) {
        // Prevent visual catch-up jump when re-entering from below.
        scrollProg = self.progress;
        smoothProg = self.progress;
        var frameP = frameProgressFromScroll(self.progress);
        targetFrame = frameP * LAST;
        currentFrame = targetFrame;
        needsRender = true;
        wakeLoop();
      }
    }
  });

  var S = FRAME_END;

  /* On mobile the video-text tweens are skipped so the timeline would be
     extremely short — GSAP would stretch the tiny text tweens across the
     full 3900px scroll.  Anchor the timeline at position 1 so scroll
     progress maps 1:1 to timeline position on every device. */
  if (smallScreen) {
    tl.set({}, {}, 1);
  }

  /* --- Start text (AR-VR): slide off (left in LTR, right in RTL) + fade --- */
  if (phaseStart) {
    var startSlide = (mobile ? -20 : -18) * dirSign; // LTR: -, RTL: +
    tl.set(phaseStart, { opacity: 1, x: 0, force3D: true }, 0);
    tl.to(phaseStart, {
      x: startSlide + "vw",
      opacity: 0,
      duration: mobile ? 0.26 : 0.65 * S,
      ease: mobile ? "power2.out" : "power1.inOut",
      force3D: true
    }, 0);
  }

  /* --- End text (AI CORE): slides in from the opposite side of the text base.
     LTR text is on the left → enters from right (+28vw). RTL text is on the right → enters from left (−28vw). --- */
  if (phaseEnd) {
    var aiCoreStart = mobile ? 0.14 : 0.38 * S;
    var aiCoreDur = mobile ? 0.18 : 0.42 * S;
    var endFromX = (mobile ? 20 : 28) * dirSign; // LTR: +20/+28, RTL: −20/−28
    tl.fromTo(phaseEnd,
      { opacity: 0, x: endFromX + "vw", force3D: true },
      {
        opacity: 1, x: "0",
        duration: aiCoreDur,
        ease: mobile ? "power3.out" : "power2.inOut",
        force3D: true
      },
      aiCoreStart
    );
  }

  /* --- Discover CTA: drifts toward the side the start text exits to --- */
  if (discover) {
    tl.to(discover, {
      opacity: 0, x: (-8 * dirSign) + "vw", // LTR: -8 (drifts left), RTL: +8 (drifts right)
      duration: mobile ? 0.10 : 0.14 * S,
      ease: "power2.in",
      force3D: true
    }, 0);
  }

  /* --- Video-through-text transition (desktop only) --- */
  if (phaseVideoText && !smallScreen) {
    var vid = phaseVideoText.querySelector("video");
    if (vid) {
      vid.play().catch(function () { });
    }

    var heroOverlay = section.querySelector(".hero-frame-overlay");
    var remainDur = 1 - S;
    /* slide + text + reveal must sum to 1.0 — larger textFraction = slower headline per scroll */
    var slideFraction = 0.12;
    var textFraction = 0.58;
    var revealFraction = 0.30;
    var slideDur = remainDur * slideFraction;
    var textDur = remainDur * textFraction;
    var revealDur = remainDur * revealFraction;

    /* RTL flip: canvas exits to the right, video panel comes from the left,
       and the headline slides off to the right instead of the left.
       Pattern: <originalLtrValue> * dirSign (dirSign = 1 in LTR, -1 in RTL). */
    var exitPct = -100 * dirSign;          // LTR: -100 (canvas exits left), RTL: +100 (canvas exits right)
    var enterFromPct = 100 * dirSign;      // LTR: +100 (video enters from right), RTL: -100 (video enters from left)
    var headlineExitPct = -105 * dirSign;  // LTR: -105 (headline slides off left), RTL: +105 (headline slides off right)

    tl.set(phaseVideoText, { visibility: "visible", xPercent: enterFromPct }, 0);

    tl.to(canvas, { xPercent: exitPct, duration: slideDur, ease: "none", force3D: true }, S);
    if (heroOverlay) {
      tl.to(heroOverlay, { xPercent: exitPct, duration: slideDur, ease: "none", force3D: true }, S);
    }
    tl.to(phaseVideoText, { xPercent: 0, duration: slideDur, ease: "none", force3D: true }, S);

    var videoTextH1 = phaseVideoText.querySelector(".hero-video-text-mask h1");
    var videoTextMask = phaseVideoText.querySelector(".hero-video-text-mask");
    var textStart = S + slideDur;

    if (videoTextH1) {
      gsap.set(videoTextH1, { xPercent: 0 });
      tl.to(videoTextH1, {
        xPercent: headlineExitPct,
        duration: textDur,
        ease: "power1.inOut"
      }, textStart);
    }

    if (videoTextMask && videoTextH1) {
      var revealStart = textStart + textDur * 0.62;
      tl.to(videoTextH1, { opacity: 0, duration: revealDur * 0.25, ease: "power2.in" }, revealStart);
      tl.to(videoTextMask, {
        opacity: 0,
        duration: revealDur * 0.55,
        ease: "power1.inOut",
      }, revealStart);
    }
  }

  /* ═══════════════════════════════════════════
     RESIZE
     ═══════════════════════════════════════════ */
  var rTimer;
  window.addEventListener("resize", function () {
    clearTimeout(rTimer);
    rTimer = setTimeout(function () {
      if (!useImgFallback) {
        dpr = Math.min(window.devicePixelRatio, MAX_DPR);
        resizeCanvas();
      }
      lastRenderedFrame = -999;
      mobile = window.matchMedia("(max-width: 768px)").matches;
      wakeLoop();
      ScrollTrigger.refresh();
    }, 150);
  });
})();
